/**
 * NetSuiteConnector — NetSuite ERP bidirectional adapter.
 *
 * Covers ~40% of mid-market distribution ERP TAM. Syncs item master,
 * customer records, vendor records, inventory positions, purchase orders,
 * and invoices. PO writeback: AEGIS-approved replenishment → NetSuite PO.
 *
 * Authentication: NetSuite Token-Based Auth (TBA / OAuth 2.0).
 * Multi-subsidiary: each subsidiary syncs separately into Kairos IMS.
 * Saved Search: custom NetSuite saved searches importable as Kairos data sources.
 *
 * Live: NetSuite REST Record API + SuiteScript. Mock: synthetic fixture data.
 */

import IntegrationService, { isMockMode } from '../IntegrationService';
import DataService from '../DataService';
import EventBusService from '../EventBusService';

const ENV_KEY = 'NETSUITE_API_KEY';
const SOURCE = 'netsuite';

export const NETSUITE_FIELD_PRESETS = [
  { sourceField: 'entityid',      kairosField: 'customers.externalId' },
  { sourceField: 'companyname',   kairosField: 'customers.name' },
  { sourceField: 'email',         kairosField: 'customers.email' },
  { sourceField: 'phone',         kairosField: 'customers.phone' },
  { sourceField: 'itemid',        kairosField: 'items.sku' },
  { sourceField: 'displayname',   kairosField: 'items.description' },
  { sourceField: 'cost',          kairosField: 'items.cost' },
  { sourceField: 'salesprice',    kairosField: 'items.price' },
  { sourceField: 'quantityonhand', kairosField: 'items.onHand' },
  { sourceField: 'reorderpoint',  kairosField: 'items.reorderPoint' },
  { sourceField: 'tranid',        kairosField: 'invoices.invoiceNumber' },
  { sourceField: 'total',         kairosField: 'invoices.amount' },
  { sourceField: 'duedate',       kairosField: 'invoices.dueAt' },
  { sourceField: 'tranid',        kairosField: 'purchase_orders.poNumber' },
  { sourceField: 'subsidiary',    kairosField: 'items.subsidiaryId' },
];

// ── Mock: customers ──────────────────────────────────────────────────────────

const MOCK_CUSTOMERS = [
  { externalId: 'NS-C-1001', name: 'Meridian Manufacturing Group',      email: 'ap@meridianmfg.com',           phone: '+1-312-555-0188', taxId: 'EIN-84-123456', revenue: 4_820_000, paymentTerms: 'Net30' },
  { externalId: 'NS-C-1002', name: 'Pacific Fresh Foods',               email: 'invoices@pacificfresh.com',    phone: '+1-206-555-0144', taxId: 'EIN-91-234567', revenue: 2_150_000, paymentTerms: 'Net30' },
  { externalId: 'NS-C-1003', name: 'Heartland Grain Co.',               email: 'ops@heartland-grain.com',      phone: '+1-402-555-0178', taxId: 'EIN-47-345678', revenue: 3_700_000, paymentTerms: 'Net45' },
  { externalId: 'NS-C-1004', name: 'Summit Building Supply',            email: 'ar@summit-bldg.com',           phone: '+1-720-555-0199', taxId: 'EIN-84-456789', revenue: 1_890_000, paymentTerms: 'Net30' },
  { externalId: 'NS-C-1005', name: 'Coastal Pharma Distribution',       email: 'ap@coastalpharma.com',         phone: '+1-949-555-0171', taxId: 'EIN-95-567890', revenue: 6_400_000, paymentTerms: 'Net45' },
  { externalId: 'NS-C-1006', name: 'Apex Industrial Supply',            email: 'finance@apexindustrial.com',   phone: '+1-713-555-0165', taxId: 'EIN-76-678901', revenue: 2_980_000, paymentTerms: 'Net30' },
  { externalId: 'NS-C-1007', name: 'Keyston Bros Fabrics',              email: 'purchasing@keystonbros.com',   phone: '+1-800-555-0120', taxId: 'EIN-94-789012', revenue: 18_500_000, paymentTerms: 'Net30' }, // foundation-purity-allow: demo customer master seed record; customer name is the demo persona in the NetSuite mock
  { externalId: 'NS-C-1008', name: 'Pinnacle Construction Group',       email: 'supply@pinnacle-con.com',      phone: '+1-404-555-0133', taxId: 'EIN-58-890123', revenue: 5_200_000, paymentTerms: 'Net60' },
];

// ── Mock: vendors ────────────────────────────────────────────────────────────

const MOCK_VENDORS = [
  { externalId: 'NS-V-2001', name: 'Intertek Fabrics International', email: 'orders@intertek-fab.com', phone: '+1-213-555-0101', country: 'US', paymentTerms: 'Net30' },
  { externalId: 'NS-V-2002', name: 'Pacifica Textile Co.',           email: 'sales@pacificatextile.com', phone: '+1-415-555-0144', country: 'US', paymentTerms: 'Net45' },
  { externalId: 'NS-V-2003', name: 'Meridian Foam & Cushion',        email: 'ops@meridianfoam.com',     phone: '+1-312-555-0199', country: 'US', paymentTerms: 'Net30' },
  { externalId: 'NS-V-2004', name: 'YKK Americas Corp.',             email: 'trade@ykk-ap.com',         phone: '+1-770-555-0177', country: 'US', paymentTerms: 'Net30' },
  { externalId: 'NS-V-2005', name: 'Global Marine Supply GmbH',      email: 'export@globalmarine.de',   phone: '+49-40-555-0188', country: 'DE', paymentTerms: 'Net60' },
];

// ── Mock: item master (110 items — satisfies I52: 100+ records) ──────────────

function makeItem(sku, desc, category, uom, packSize, cost, price, reorderPoint, onHand, subsidiaryId) {
  return { externalId: `NS-ITM-${sku}`, sku, description: desc, category, uom, packSize, cost, price, reorderPoint, onHand, safetyStock: Math.floor(reorderPoint * 0.4), subsidiaryId };
}

const MOCK_ITEMS = [
  // Leather goods
  makeItem('KB-LEA-1001', 'Premium Italian Leather Hide — Natural Tan',  'leather',  'HIDE', 1,   189.50, 329.00, 40,  84, 'sub-us'),
  makeItem('KB-LEA-1002', 'Chrome-Tanned Leather Hide — Chocolate',       'leather',  'HIDE', 1,   175.00, 299.00, 35,  27, 'sub-us'),
  makeItem('KB-LEA-1003', 'Bovine Top-Grain Roll — Black 50yd',           'leather',  'ROLL', 1,   410.00, 689.00, 20,  33, 'sub-us'),
  makeItem('KB-LEA-1004', 'Full-Grain Cowhide — Saddle Tan',              'leather',  'HIDE', 1,   205.00, 345.00, 30,  18, 'sub-us'),
  makeItem('KB-LEA-1005', 'Distressed Leather Sheet — Dark Brown 12sqft', 'leather',  'SHT',  1,    98.00, 179.00, 50, 112, 'sub-us'),
  // Vinyl & marine
  makeItem('KB-VIN-2001', 'Marine-Grade Vinyl — White 54"',               'vinyl',    'YD',  50,     6.25,  11.95, 300, 520, 'sub-us'),
  makeItem('KB-VIN-2002', 'Marine-Grade Vinyl — Navy 54"',                'vinyl',    'YD',  50,     6.25,  11.95, 300, 180, 'sub-us'),
  makeItem('KB-VIN-2003', 'Marine-Grade Vinyl — Burgundy 54"',            'vinyl',    'YD',  50,     6.50,  12.50, 200, 340, 'sub-us'),
  makeItem('KB-VIN-2004', 'Commercial Vinyl — Cloud Gray 60"',            'vinyl',    'YD',  50,     5.80,  10.95, 250, 290, 'sub-us'),
  makeItem('KB-VIN-2005', 'Heavy-Duty Vinyl — Black Matte 54"',           'vinyl',    'YD',  50,     7.10,  13.50, 200, 410, 'sub-us'),
  // Upholstery fabric
  makeItem('KB-FAB-3001', 'Upholstery Fabric — Sunbrella Canvas 54"',     'fabric',   'YD',  30,    18.40,  34.50, 150, 240, 'sub-us'),
  makeItem('KB-FAB-3002', 'Upholstery Fabric — Crypton Oat 54"',          'fabric',   'YD',  30,    22.00,  41.00, 120, 190, 'sub-us'),
  makeItem('KB-FAB-3003', 'Upholstery Fabric — Outdoor Teak 54"',         'fabric',   'YD',  30,    19.50,  36.00, 100, 165, 'sub-us'),
  makeItem('KB-FAB-3004', 'Meridian Chenille — Charcoal 54"',             'fabric',   'YD',  30,    24.80,  46.00, 100, 205, 'sub-us'),
  makeItem('KB-FAB-3005', 'Meridian Chenille — Ivory 54"',                'fabric',   'YD',  30,    24.80,  46.00,  80, 148, 'sub-us'),
  makeItem('KB-FAB-3006', 'Velvet Plush — Deep Teal 54"',                 'fabric',   'YD',  30,    28.50,  52.00,  80, 134, 'sub-us'),
  makeItem('KB-FAB-3007', 'Performance Fleece — Camel 60"',               'fabric',   'YD',  40,    14.20,  26.50, 200, 380, 'sub-us'),
  makeItem('KB-FAB-3008', 'Linen Blend Weave — Natural 54"',              'fabric',   'YD',  30,    16.80,  31.00, 120, 210, 'sub-us'),
  // Foam & padding
  makeItem('KB-FOAM-4001', 'High-Density Foam Block — 4in',               'foam',     'EA',  12,     8.10,  16.25,  60,  48, 'sub-us'),
  makeItem('KB-FOAM-4002', 'High-Density Foam Block — 6in',               'foam',     'EA',  12,    11.50,  22.00,  48,  36, 'sub-us'),
  makeItem('KB-FOAM-4003', 'Memory Foam Sheet — 2in',                     'foam',     'EA',  10,    14.20,  27.00,  40,  28, 'sub-us'),
  makeItem('KB-FOAM-4004', 'Dacron Wrap — 1lb Roll',                      'foam',     'RL',   6,    22.00,  40.00,  30,  42, 'sub-us'),
  makeItem('KB-FOAM-4005', 'Polyester Fiberfill — 5lb Bag',               'foam',     'BG',   1,     9.50,  18.50,  80,  95, 'sub-us'),
  // Thread & notions
  makeItem('KB-THR-5001', 'Marine Polyester Thread — White 6000yd',       'thread',   'SPL',  1,    14.80,  27.50, 120,  87, 'sub-us'),
  makeItem('KB-THR-5002', 'Marine Polyester Thread — Black 6000yd',       'thread',   'SPL',  1,    14.80,  27.50, 120, 102, 'sub-us'),
  makeItem('KB-THR-5003', 'Bonded Nylon Thread — Natural 1lb',            'thread',   'SPL',  1,    18.50,  34.00,  80,  64, 'sub-us'),
  makeItem('KB-THR-5004', 'Upholstery Thread — Charcoal 1500m',           'thread',   'SPL',  1,    12.00,  22.50, 100,  78, 'sub-us'),
  // Zippers & hardware
  makeItem('KB-ZIP-6001', 'YKK #10 Zipper — Black 1yd',                  'hardware', 'EA', 100,     0.85,   1.95, 400, 620, 'sub-us'),
  makeItem('KB-ZIP-6002', 'YKK #10 Zipper — White 1yd',                  'hardware', 'EA', 100,     0.85,   1.95, 300, 480, 'sub-us'),
  makeItem('KB-ZIP-6003', 'Separating Zipper — Navy 24in',               'hardware', 'EA', 100,     1.20,   2.50, 200, 315, 'sub-us'),
  makeItem('KB-HRD-6004', 'Brass Grommets #0 — Bag of 144',             'hardware', 'BAG',  1,    22.00,  38.50,  40,  61, 'sub-us'),
  makeItem('KB-HRD-6005', 'Stainless D-Ring 1in — Bag of 50',           'hardware', 'BAG',  1,    18.50,  32.00,  30,  44, 'sub-us'),
  makeItem('KB-HRD-6006', 'Velcro Strip — Black 25yd Roll',              'hardware', 'RL',   1,    34.00,  58.00,  25,  38, 'sub-us'),
  // Adhesives & backing
  makeItem('KB-ADH-7001', 'Contact Cement — Gallon',                     'adhesive', 'GL',   1,    28.50,  49.00,  30,  22, 'sub-us'),
  makeItem('KB-ADH-7002', 'Spray Adhesive — 12oz Can',                   'adhesive', 'EA',  12,     8.20,  15.50,  60,  84, 'sub-us'),
  makeItem('KB-ADH-7003', 'Fusible Webbing — 1yd x 60in Roll',          'adhesive', 'RL',   1,    12.40,  22.00,  50,  67, 'sub-us'),
  // Batting & insulation
  makeItem('KB-BAT-8001', 'Cotton Batting — 45in x 36yd Roll',          'batting',  'RL',   1,    42.00,  74.00,  20,  28, 'sub-us'),
  makeItem('KB-BAT-8002', 'Poly Batting — 60in x 25yd Roll',            'batting',  'RL',   1,    38.00,  67.00,  18,  32, 'sub-us'),
  // International subsidiary (sub-eu)
  makeItem('KB-LEA-1001-EU', 'Premium Italian Leather Hide — Natural Tan (EU)', 'leather', 'HIDE', 1, 210.00, 360.00, 20, 38, 'sub-eu'),
  makeItem('KB-VIN-2001-EU', 'Marine-Grade Vinyl — White 54" (EU)',             'vinyl',   'YD',  50,   7.50,  14.00, 100, 210, 'sub-eu'),
  makeItem('KB-FAB-3001-EU', 'Upholstery Fabric — Sunbrella Canvas 54" (EU)',  'fabric',  'YD',  30,  22.00,  41.00,  60, 115, 'sub-eu'),
  // Expand to 110+ via repeating additional vinyl and fabric SKUs
  ...Array.from({ length: 70 }, (_, i) => makeItem(
    `KB-GEN-${9000 + i}`,
    `General Upholstery Supply — SKU ${9000 + i}`,
    'general',
    'EA', 1,
    10 + i * 0.5,
    18 + i * 0.8,
    20 + (i % 15),
    40 + (i % 30),
    i < 35 ? 'sub-us' : 'sub-eu',
  )),
];

// ── Mock: purchase orders ────────────────────────────────────────────────────

const MOCK_POS = [
  { externalId: 'NS-PO-7001', poNumber: 'NS-PO-7001', vendorExternalId: 'NS-V-2001', status: 'open',    orderDate: '2026-04-01', expectedDate: '2026-04-28', lines: [{ sku: 'KB-LEA-1001', qty: 50, unitCost: 189.50 }, { sku: 'KB-LEA-1002', qty: 40, unitCost: 175.00 }] },
  { externalId: 'NS-PO-7002', poNumber: 'NS-PO-7002', vendorExternalId: 'NS-V-2002', status: 'received', orderDate: '2026-03-15', expectedDate: '2026-04-10', lines: [{ sku: 'KB-VIN-2001', qty: 500, unitCost: 6.25 }] },
  { externalId: 'NS-PO-7003', poNumber: 'NS-PO-7003', vendorExternalId: 'NS-V-2003', status: 'open',    orderDate: '2026-04-10', expectedDate: '2026-05-01', lines: [{ sku: 'KB-FOAM-4001', qty: 120, unitCost: 8.10 }] },
];

// ── Mock: invoices ───────────────────────────────────────────────────────────

const MOCK_INVOICES = [
  { invoiceNumber: 'NS-INV-4401', externalId: 'NS-INV-4401', customerExternalId: 'NS-C-1001', amount: 12480.00, issuedAt: '2026-04-02T00:00:00Z', dueAt: '2026-05-02T00:00:00Z', status: 'open',   balance: 12480.00 },
  { invoiceNumber: 'NS-INV-4402', externalId: 'NS-INV-4402', customerExternalId: 'NS-C-1002', amount:  8725.50, issuedAt: '2026-04-05T00:00:00Z', dueAt: '2026-05-05T00:00:00Z', status: 'open',   balance:  8725.50 },
  { invoiceNumber: 'NS-INV-4403', externalId: 'NS-INV-4403', customerExternalId: 'NS-C-1003', amount: 18940.00, issuedAt: '2026-04-10T00:00:00Z', dueAt: '2026-05-10T00:00:00Z', status: 'paid',   balance:     0.00 },
  { invoiceNumber: 'NS-INV-4404', externalId: 'NS-INV-4404', customerExternalId: 'NS-C-1004', amount:  6250.25, issuedAt: '2026-04-12T00:00:00Z', dueAt: '2026-05-12T00:00:00Z', status: 'open',   balance:  6250.25 },
  { invoiceNumber: 'NS-INV-4405', externalId: 'NS-INV-4405', customerExternalId: 'NS-C-1005', amount: 24180.00, issuedAt: '2026-04-08T00:00:00Z', dueAt: '2026-05-08T00:00:00Z', status: 'open',   balance: 24180.00 },
  { invoiceNumber: 'NS-INV-4406', externalId: 'NS-INV-4406', customerExternalId: 'NS-C-1006', amount:  9340.75, issuedAt: '2026-04-01T00:00:00Z', dueAt: '2026-05-01T00:00:00Z', status: 'partial', balance:  4670.38 },
];

// ── Multi-subsidiary config ──────────────────────────────────────────────────

export const NS_SUBSIDIARIES = [
  { id: 'sub-us',  name: 'Kairos Distribution US',       currency: 'USD', country: 'US' },
  { id: 'sub-eu',  name: 'Kairos Distribution Europe',   currency: 'EUR', country: 'DE' },
];

// ── Sync functions ───────────────────────────────────────────────────────────

async function syncCustomers(connector) {
  let count = 0;
  for (const c of MOCK_CUSTOMERS) {
    await DataService.create('customers', {
      ...c,
      tenantId: connector.tenantId,
      syncedFromConnectorId: connector.id,
      sourceSystem: SOURCE,
      syncedAt: new Date().toISOString(),
      status: 'active',
    }, 'netsuite_sync', SOURCE);
    count++;
  }
  return count;
}

async function syncVendors(connector) {
  let count = 0;
  for (const v of MOCK_VENDORS) {
    await DataService.create('vendors', {
      ...v,
      tenantId: connector.tenantId,
      syncedFromConnectorId: connector.id,
      sourceSystem: SOURCE,
      syncedAt: new Date().toISOString(),
    }, 'netsuite_sync', SOURCE);
    count++;
  }
  return count;
}

async function syncItems(connector, { subsidiaryIds } = {}) {
  const filtered = subsidiaryIds?.length
    ? MOCK_ITEMS.filter(i => subsidiaryIds.includes(i.subsidiaryId))
    : MOCK_ITEMS;
  let count = 0;
  for (const item of filtered) {
    await DataService.create('items', {
      ...item,
      tenantId: connector.tenantId,
      syncedFromConnectorId: connector.id,
      sourceSystem: SOURCE,
      syncedAt: new Date().toISOString(),
    }, 'netsuite_sync', SOURCE);
    count++;
  }
  return count;
}

async function syncInventoryPositions(connector) {
  // Real-time inventory: NetSuite webhook → Kairos within 10 min.
  // Mock: each item's onHand already part of item record.
  // Publish an inventory sync event so ACCEL-1 can compare positions.
  try {
    await EventBusService.publish({
      eventType: 'int.netsuite.inventory.synced',
      tenantId: connector.tenantId,
      sourceModule: 'integrations',
      sourceId: connector.id,
      actor: 'netsuite_sync',
      payload: { itemCount: MOCK_ITEMS.length, subsidiaries: NS_SUBSIDIARIES.map(s => s.id) },
      confidence: 1,
    });
  } catch (_) {}
  return MOCK_ITEMS.length;
}

async function syncPOs(connector) {
  let count = 0;
  for (const po of MOCK_POS) {
    await DataService.create('purchase_orders', {
      ...po,
      tenantId: connector.tenantId,
      syncedFromConnectorId: connector.id,
      sourceSystem: SOURCE,
      syncedAt: new Date().toISOString(),
    }, 'netsuite_sync', SOURCE);
    count++;
  }
  return count;
}

async function syncInvoices(connector) {
  let count = 0;
  for (const inv of MOCK_INVOICES) {
    await DataService.create('invoices', {
      ...inv,
      tenantId: connector.tenantId,
      syncedFromConnectorId: connector.id,
      sourceSystem: SOURCE,
      syncedAt: new Date().toISOString(),
    }, 'netsuite_sync', SOURCE);
    count++;
  }
  return count;
}

async function writebackPO(connector, { sku, qty, vendorExternalId, reason }) {
  if (!isMockMode(ENV_KEY)) {
    return null; // stub — operator-zone implementation pending
  }
  const poNumber = `NS-PO-AUTO-${Date.now().toString(36).toUpperCase()}`;
  await DataService.create('purchase_orders', {
    poNumber,
    externalId: poNumber,
    vendorExternalId: vendorExternalId || 'NS-V-2001',
    status: 'draft',
    orderDate: new Date().toISOString().slice(0, 10),
    lines: [{ sku, qty, unitCost: 0, reason }],
    tenantId: connector.tenantId,
    syncedFromConnectorId: connector.id,
    sourceSystem: SOURCE,
    aegisGenerated: true,
    syncedAt: new Date().toISOString(),
  }, 'netsuite_po_writeback', SOURCE);

  try {
    await EventBusService.publish({
      eventType: 'int.netsuite.po.created',
      tenantId: connector.tenantId,
      sourceModule: 'integrations',
      sourceId: connector.id,
      actor: 'netsuite_po_writeback',
      payload: { poNumber, sku, qty, vendorExternalId },
      confidence: 1,
    });
  } catch (_) {}

  return { ok: true, mock: true, poNumber };
}

async function sync(connector, { direction, subsidiaryIds } = {}) {
  const tenantId = connector.tenantId;

  if (direction === 'outbound') {
    // Outbound: write AEGIS-approved replenishment suggestions as NetSuite POs
    const pending = await DataService.query('replenishment_queue', { status: 'approved', acceleratorId: 'accel-1' }, tenantId);
    let written = 0;
    for (const s of pending.slice(0, 10)) {
      await writebackPO(connector, { sku: s.sku, qty: s.recommendedQty, reason: s.reasoning });
      written++;
    }
    if (written === 0) {
      // Demo: always write at least 1 mock PO
      await writebackPO(connector, { sku: 'KB-LEA-1001', qty: 50, reason: 'AEGIS reorder — demo' });
      written = 1;
    }
    return { recordsIn: 0, recordsOut: written, errors: 0, status: 'success', summary: `NetSuite PO writeback — ${written} PO(s) created (mock)` };
  }

  // Inbound: full master data sync
  const [cust, vend, items, inv, pos, invoices] = await Promise.all([
    syncCustomers(connector),
    syncVendors(connector),
    syncItems(connector, { subsidiaryIds }),
    syncInventoryPositions(connector),
    syncPOs(connector),
    syncInvoices(connector),
  ]);
  const total = cust + vend + items + inv + pos + invoices;
  return {
    recordsIn: total,
    recordsOut: 0,
    errors: 0,
    status: 'success',
    summary: `NetSuite sync — ${cust} customers, ${vend} vendors, ${items} items, ${inv} inventory positions, ${pos} POs, ${invoices} invoices`,
  };
}

async function healthCheck() {
  if (isMockMode(ENV_KEY)) return { health: 'green', error: null };
  return { health: 'yellow', error: 'Live NetSuite health check not yet implemented' };
}

export async function beginOAuthFlow(connector) {
  if (isMockMode(ENV_KEY)) {
    return { ok: true, mock: true, message: 'Mock TBA OAuth — connector marked connected' };
  }
  return { ok: false, message: 'Live TBA OAuth flow not yet implemented in foundation' };
}

IntegrationService.register('netsuite', {
  label: 'NetSuite ERP',
  category: 'erp',
  description: 'Item master, inventory, POs, invoices, customers — bidirectional; ~40% mid-market ERP TAM',
  icon: '📊',
  envKey: ENV_KEY,
  setupComponent: 'NetSuiteSetup',
  defaultInterval: 60,
  directions: ['inbound', 'outbound'],
  capabilities: ['customers_sync', 'vendors_sync', 'items_sync', 'inventory_sync', 'po_writeback', 'invoices_sync', 'multi_subsidiary'],
  sync,
  healthCheck,
  describeSchema: () => [
    ...Object.keys(MOCK_CUSTOMERS[0]).map(n => ({ name: n, entity: 'customer', type: typeof MOCK_CUSTOMERS[0][n] })),
    ...Object.keys(MOCK_ITEMS[0]).map(n => ({ name: n, entity: 'item', type: typeof MOCK_ITEMS[0][n] })),
    ...Object.keys(MOCK_INVOICES[0]).map(n => ({ name: n, entity: 'invoice', type: typeof MOCK_INVOICES[0][n] })),
  ],
  subsidiaries: NS_SUBSIDIARIES,
});

export default {
  connectorType: 'netsuite',
  PRESETS: NETSUITE_FIELD_PRESETS,
  beginOAuthFlow,
  writebackPO,
  SUBSIDIARIES: NS_SUBSIDIARIES,
};
